import * as React from 'react';
import { type VariantProps, cva } from 'class-variance-authority';
import { Toast as ToastPrimitives } from 'radix-ui';
import { type LucideIcon, X } from 'lucide-react';
import { cn } from '@/lib/utils';

const ToastProvider = ToastPrimitives.Provider;

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      'fixed bottom-12 left-1/2 top-auto z-100 flex max-h-screen w-full max-w-[622px] -translate-x-1/2 flex-col-reverse p-4',
      className,
    )}
    data-cy="toast-viewport"
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

export enum ToastVariant {
  DEFAULT = 'default',
  DESTRUCTIVE = 'destructive',
  SUCCESS = 'success',
}

const toastVariants = cva(
  'group pointer-events-auto relative mt-3 flex w-full justify-between gap-x-3 overflow-hidden rounded-2xl p-[18px] pr-14 shadow-[3px_3px_12px_0px_rgba(0,0,0,0.25)] transition-all focus-visible:outline-hidden data-[state=closed]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-bottom-full data-[state=open]:animate-in data-[state=open]:slide-in-from-top-full data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-(--radix-toast-swipe-end-x) data-[swipe=end]:animate-out data-[swipe=move]:translate-x-(--radix-toast-swipe-move-x) data-[swipe=move]:transition-none sm:data-[state=open]:slide-in-from-bottom-full',
  {
    variants: {
      variant: {
        [ToastVariant.DEFAULT]: 'bg-ui-light text-text-inverted',
        [ToastVariant.DESTRUCTIVE]: 'destructive group bg-status-failure text-text-inverted',
        [ToastVariant.SUCCESS]: 'success group bg-status-success text-text-inverted',
      },
    },
    defaultVariants: {
      variant: ToastVariant.DEFAULT,
    },
  },
);

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> & VariantProps<typeof toastVariants>
>(({ className, variant, ...props }, ref) => (
  <ToastPrimitives.Root
    ref={ref}
    className={cn(toastVariants({ variant }), className)}
    data-cy="toast-root"
    {...props}
  />
));
Toast.displayName = ToastPrimitives.Root.displayName;

interface ToastActionProps extends React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action> {
  icon?: LucideIcon;
}

const ToastAction = React.forwardRef<React.ElementRef<typeof ToastPrimitives.Action>, ToastActionProps>(
  ({ className, icon: Icon, children, ...props }, ref) => {
    return (
      <ToastPrimitives.Action
        ref={ref}
        className={cn(
          'inline-flex h-12 shrink-0 items-center justify-center rounded-xl bg-ui-base px-3 text-sm font-bold text-text-gray transition-colors hover:bg-ui-medium focus:outline-hidden disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:bg-status-failure-dark group-[.success]:bg-status-success-dark group-[.destructive]:text-text-inverted group-[.success]:text-text-inverted hover:group-[.destructive]:bg-status-failure-hover hover:group-[.success]:bg-status-success-hover dark:hover:bg-ui-panel',
          className,
          { 'gap-x-[10px]': Icon },
        )}
        data-cy="toast-action-button"
        {...props}
      >
        {Icon && <Icon className="icon-6" data-cy="toast-action-button-icon" />}
        {children}
      </ToastPrimitives.Action>
    );
  },
);
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      'absolute right-[14px] top-5 rounded-md p-1 text-icon-base opacity-50 transition-opacity duration-150 hover:text-icon-base hover:opacity-100 focus:opacity-100 focus:outline-hidden group-[.destructive]:text-text-inverted group-[.success]:text-text-inverted hover:group-[.destructive]:text-text-inverted hover:group-[.success]:text-text-inverted',
      className,
    )}
    toast-close=""
    data-cy="toast-close-button"
    {...props}
  >
    <X className="icon-6" />
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn(
      'mt-[-3px] text-sm font-bold leading-6 text-text-base group-[.destructive]:text-text-inverted group-[.success]:text-text-inverted [&+div]:text-xs',
      className,
    )}
    data-cy="toast-title"
    {...props}
  />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn(
      'text-sm text-text-gray wrap-anywhere group-[.destructive]:text-text-inverted group-[.success]:text-text-inverted',
      className,
    )}
    data-cy="toast-description"
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;

type ToastActionElement = React.ReactElement<typeof ToastAction>;

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
};
